import { Component, inject } from "@angular/core";
import {
  FormBuilder,
  FormsModule,
  ReactiveFormsModule,
  Validators
} from "@angular/forms";
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogContent,
  MatDialogRef
} from "@angular/material/dialog";
import {
  MatFormField,
  MatLabel,
  MatError,
  MatHint
} from "@angular/material/form-field";
import { MatInput } from "@angular/material/input";
import { MatButton } from "@angular/material/button";
import { CdkScrollable } from "@angular/cdk/scrolling";
import { CommonModule } from "@angular/common";
import { DialogHeaderComponent } from "@app/view/shared/dialog-header/dialog-header.component";
import {
  DesignerVisitDto,
  DesignerVisitScheduleDto
} from "@app/services/api/v1/visitDesigner/dto/designer-dto";

/** Tolerance used to mirror the server-side {@code QuantityMath.EPSILON}. */
export const QUANTITY_EPSILON = 0.0001;

export interface QuantityDragDialogData {
  source: DesignerVisitDto;
  target: DesignerVisitDto;
  schedule: DesignerVisitScheduleDto;
  /** True when the source visit's status means the source row will be archived as a ghost. */
  sourceWillArchive: boolean;
}

export interface QuantityDragDialogResult {
  /** Quantity to move from source to target. */
  quantity: number;
  /** True if the source still has remaining active quantity after the move (split). */
  sourceWillBeSplit: boolean;
  /** True if the source row will be archived as a ghost (completed/locked-source carry forward). */
  sourceWillArchive: boolean;
}

/**
 * Confirmation dialog shown before a designer drag is staged.
 *
 * <p>Defaults to moving the full remaining active quantity. The user can move a partial amount
 * (e.g. {@code 0.5}); the dialog enforces the same hard-block rules as the server (must be > 0,
 * must be <= source active quantity).
 */
@Component({
  selector: "app-quantity-drag-dialog",
  standalone: true,
  templateUrl: "./quantity-drag-dialog.component.html",
  styleUrls: ["./quantity-drag-dialog.component.scss"],
  imports: [
    CommonModule,
    CdkScrollable,
    MatDialogContent,
    MatDialogActions,
    FormsModule,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatInput,
    MatError,
    MatHint,
    MatButton,
    DialogHeaderComponent
  ]
})
export class QuantityDragDialogComponent {
  data = inject<QuantityDragDialogData>(MAT_DIALOG_DATA);
  private dialogRef =
    inject<MatDialogRef<QuantityDragDialogComponent, QuantityDragDialogResult>>(
      MatDialogRef
    );
  private fb = inject(FormBuilder);

  readonly maxQuantity: number;
  readonly form;

  constructor() {
    this.maxQuantity = this.data.schedule.quantity ?? 0;
    this.form = this.fb.nonNullable.group({
      quantity: [
        this.maxQuantity,
        [Validators.required, this.quantityValidator()]
      ]
    });
  }

  /** Validator reproducing the server hard-block: `0 < quantity <= maxQuantity`. */
  private quantityValidator() {
    return (control: { value: number | null }) => {
      const v = Number(control.value);
      if (Number.isNaN(v)) {
        return { invalid: true };
      }
      if (v <= QUANTITY_EPSILON) {
        return { tooSmall: true };
      }
      if (v - this.maxQuantity > QUANTITY_EPSILON) {
        return { tooLarge: true };
      }
      return null;
    };
  }

  get sourceWillBeSplit(): boolean {
    const v = Number(this.form.controls.quantity.value);
    return v > 0 && this.maxQuantity - v > QUANTITY_EPSILON;
  }

  confirm(): void {
    if (this.form.invalid) {
      return;
    }
    const quantity = Number(this.form.controls.quantity.value);
    this.dialogRef.close({
      quantity,
      sourceWillBeSplit: this.sourceWillBeSplit,
      sourceWillArchive: this.data.sourceWillArchive
    });
  }

  cancel(): void {
    this.dialogRef.close(undefined);
  }
}
